/**
 * Category Icons & Colors Utility
 *
 * Maps restaurant category keys to Lucide icons and Chakra UI color schemes.
 * Used by UnifiedChip and other components that display category badges.
 *
 * Category keys match the backend's CategoryOption model (snake_case).
 */

import type { LucideIcon } from 'lucide-react';
import {
  UtensilsCrossed,
  Beer,
  Wine,
  Coffee,
  Martini,
  Pizza,
  Sandwich,
  ChefHat,
  IceCream,
  Store,
  Soup,
  Salad,
  CakeSlice,
  Utensils,
  GlassWater,
} from 'lucide-react';

// =============================================================================
// Category Icon Mapping
// =============================================================================

/**
 * Maps restaurant category keys to their corresponding Lucide icon component.
 * Keys are snake_case to match backend CategoryOption.key values.
 */
export const CATEGORY_ICONS: Record<string, LucideIcon> = {
  fine_dining: UtensilsCrossed,
  tavern: Beer,
  bar_pub: Beer,
  cafe: Coffee,
  cocktail_bar: Martini,
  pizza: Pizza,
  fast_casual: Sandwich,
  bakery: ChefHat,
  ice_cream: IceCream,
  food_truck: Store,
  ramen_noodle: Soup,
  salad_healthy: Salad,
  patisserie: CakeSlice,
  casual_dining: Utensils,
  juice_smoothie: GlassWater,
  wine_bar: Wine,
};

// =============================================================================
// Category Color Mapping
// =============================================================================

interface CategoryColorScheme {
  /** Text/icon color (Chakra token) */
  color: string;
  /** CSS gradient for background (used via `background` prop) */
  gradient: string;
  /** Box-shadow for depth (CSS value) */
  boxShadow: string;
}

/** Default color scheme used when a category key is not found */
const DEFAULT_CATEGORY_COLORS: CategoryColorScheme = {
  color: 'white',
  gradient: 'linear-gradient(135deg, #9CA3AF, #4B5563)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
};

/**
 * Maps restaurant category keys to gradient color schemes.
 * Each category gets a 135deg gradient from a lighter to darker shade
 * for a premium, depth-rich chip appearance.
 */
export const CATEGORY_COLORS: Record<string, CategoryColorScheme> = {
  fine_dining:    { color: 'white', gradient: 'linear-gradient(135deg, #A78BFA, #5B21B6)', boxShadow: '0 2px 8px rgba(91,33,182,0.2)' },
  tavern:         { color: 'white', gradient: 'linear-gradient(135deg, #FB923C, #C2410C)', boxShadow: '0 2px 8px rgba(194,65,12,0.2)' },
  bar_pub:        { color: 'white', gradient: 'linear-gradient(135deg, #FB923C, #C2410C)', boxShadow: '0 2px 8px rgba(194,65,12,0.2)' },
  cafe:           { color: 'white', gradient: 'linear-gradient(135deg, #FACC15, #A16207)', boxShadow: '0 2px 8px rgba(161,98,7,0.2)' },
  cocktail_bar:   { color: 'white', gradient: 'linear-gradient(135deg, #F472B6, #BE185D)', boxShadow: '0 2px 8px rgba(190,24,93,0.2)' },
  pizza:          { color: 'white', gradient: 'linear-gradient(135deg, #F87171, #B91C1C)', boxShadow: '0 2px 8px rgba(185,28,28,0.2)' },
  fast_casual:    { color: 'white', gradient: 'linear-gradient(135deg, #2DD4BF, #0F766E)', boxShadow: '0 2px 8px rgba(15,118,110,0.2)' },
  bakery:         { color: 'white', gradient: 'linear-gradient(135deg, #FDBA74, #C2410C)', boxShadow: '0 2px 8px rgba(194,65,12,0.2)' },
  ice_cream:      { color: 'white', gradient: 'linear-gradient(135deg, #22D3EE, #0E7490)', boxShadow: '0 2px 8px rgba(14,116,144,0.2)' },
  food_truck:     { color: 'white', gradient: 'linear-gradient(135deg, #4ADE80, #15803D)', boxShadow: '0 2px 8px rgba(21,128,61,0.2)' },
  ramen_noodle:   { color: 'white', gradient: 'linear-gradient(135deg, #F87171, #B91C1C)', boxShadow: '0 2px 8px rgba(185,28,28,0.2)' },
  salad_healthy:  { color: 'white', gradient: 'linear-gradient(135deg, #4ADE80, #15803D)', boxShadow: '0 2px 8px rgba(21,128,61,0.2)' },
  patisserie:     { color: 'white', gradient: 'linear-gradient(135deg, #F9A8D4, #BE185D)', boxShadow: '0 2px 8px rgba(190,24,93,0.2)' },
  casual_dining:  { color: 'white', gradient: 'linear-gradient(135deg, #60A5FA, #1D4ED8)', boxShadow: '0 2px 8px rgba(29,78,216,0.2)' },
  juice_smoothie: { color: 'white', gradient: 'linear-gradient(135deg, #86EFAC, #16A34A)', boxShadow: '0 2px 8px rgba(22,163,74,0.2)' },
  wine_bar:       { color: 'white', gradient: 'linear-gradient(135deg, #A78BFA, #4C1D95)', boxShadow: '0 2px 8px rgba(76,29,149,0.2)' },
};

// =============================================================================
// Accessor Functions
// =============================================================================

/**
 * Get the Lucide icon for a restaurant category key.
 * Falls back to the generic Utensils icon if the key is not mapped.
 *
 * @param key - Category key from the backend (e.g., 'fine_dining')
 * @returns The corresponding LucideIcon component
 *
 * @example
 * const Icon = getCategoryIconComponent('cocktail_bar'); // Martini
 * <Icon size={16} />
 */
export function getCategoryIconComponent(key: string): LucideIcon {
  return CATEGORY_ICONS[key] ?? Utensils;
}

/**
 * Get the color scheme for a restaurant category key.
 * Falls back to a neutral gray scheme if the key is not mapped.
 *
 * @param key - Category key from the backend (e.g., 'fine_dining')
 * @returns Object with gradient, color, and boxShadow values
 *
 * @example
 * const colors = getCategoryColors('fine_dining');
 * // { gradient: 'linear-gradient(...)', color: 'white', boxShadow: '...' }
 */
export function getCategoryColors(key: string): CategoryColorScheme {
  return CATEGORY_COLORS[key] ?? DEFAULT_CATEGORY_COLORS;
}
